import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { profileApi, referralsApi } from '../services/api';
import { Button } from '../components/ui/button';
import {
  Loader2,
  Download,
  ArrowLeft,
  Crown,
  ShieldCheck,
  Linkedin,
  Twitter,
  Link2,
  Check as CheckIcon,
} from 'lucide-react';
import Navbar from '../components/Navbar';
import AuthModal from '../components/AuthModal';

const Certificate = () => {
  const { roomId } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, loading: authLoading } = useAuth();
  const [cert, setCert] = useState(null);
  const [error, setError] = useState(null);
  const [errorStatus, setErrorStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAuth, setShowAuth] = useState(false);
  const [referralCode, setReferralCode] = useState(null);
  const [linkCopied, setLinkCopied] = useState(false);

  useEffect(() => {
    if (authLoading) return undefined;
    if (!isAuthenticated) {
      setShowAuth(true);
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const data = await profileApi.getCertificate(roomId);
        if (!cancelled) {
          setCert(data);
          setError(null);
        }
        // Best-effort: pull the user's referral code so we can embed it in
        // social share links. A missing code is fine — we just share without.
        try {
          const ref = await referralsApi.getMe();
          if (!cancelled && ref?.code) setReferralCode(ref.code);
        } catch (err) {
          // Share buttons still work without a referral code.
          console.debug('[certificate] referral fetch failed:', err?.message);
        }
      } catch (e) {
        if (!cancelled) {
          setError(e.message || 'Could not load certificate');
          setErrorStatus(e.status || null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [roomId, isAuthenticated, authLoading]);

  const handlePrint = () => {
    window.print();
  };

  // Build share payload once the cert is loaded.
  // We share the backend OG landing page (server-rendered og: meta + image)
  // so LinkedIn/X/Slack/Discord can actually unfurl a personalized cert
  // preview. The landing page auto-redirects real users to the on-site
  // certificate page within ~1s.
  const shareUrl = (() => {
    if (typeof window === 'undefined' || !cert) return '';
    const backend = process.env.REACT_APP_BACKEND_URL || window.location.origin;
    const landing = `${backend}/api/share/cert/${encodeURIComponent(
      roomId,
    )}/${encodeURIComponent(cert.username)}`;
    return referralCode ? `${landing}?ref=${referralCode}` : landing;
  })();

  const shareText = cert
    ? `I just earned the ${cert.room.title} certificate on CyberInsti — learning to hack, defend, and analyze threats through hands-on labs. Join me:`
    : '';

  const openLinkedIn = () => {
    const url = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(
      shareUrl,
    )}`;
    window.open(url, '_blank', 'noopener,noreferrer,width=600,height=600');
  };

  const openTwitter = () => {
    const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(
      shareText,
    )}&url=${encodeURIComponent(shareUrl)}&hashtags=cybersecurity,CyberInsti`;
    window.open(url, '_blank', 'noopener,noreferrer,width=600,height=600');
  };

  const copyShareLink = async () => {
    try {
      await navigator.clipboard.writeText(
        shareText ? `${shareText} ${shareUrl}` : shareUrl,
      );
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    } catch (err) {
      // Clipboard API unavailable (older browsers / insecure origins).
      console.debug('[certificate] clipboard copy failed:', err?.message);
    }
  };

  const formatDate = (iso) => {
    if (!iso) return '';
    try {
      return new Date(iso).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    } catch (err) {
      console.debug('[certificate] date parse failed:', err?.message);
      return iso;
    }
  };

  if (loading || authLoading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] text-white flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#00FF00]" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] text-white">
        <Navbar />
        {showAuth && (
          <AuthModal
            isOpen={showAuth}
            onClose={() => setShowAuth(false)}
            initialTab="login"
          />
        )}
      </div>
    );
  }

  if (error || !cert) {
    const isProGate = errorStatus === 402 || (error || '').toLowerCase().includes('pro');
    return (
      <div className="min-h-screen bg-[#0A0A0A] text-white">
        <Navbar />
        <div className="max-w-2xl mx-auto px-4 py-24 mt-16 text-center">
          <Crown className="w-14 h-14 text-[#00FF00] mx-auto mb-4" />
          <h1 className="text-3xl font-bold mb-3">
            {isProGate ? 'Certificates are a Pro feature' : 'Certificate unavailable'}
          </h1>
          <p className="text-gray-400 mb-8" data-testid="certificate-error">
            {error || 'No certificate found for this room.'}
          </p>
          <div className="flex justify-center gap-3">
            <Button
              onClick={() => navigate(`/room/${roomId}`)}
              variant="outline"
              className="border-gray-700 text-gray-200 hover:bg-gray-800"
              data-testid="certificate-back-btn"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to room
            </Button>
            {isProGate && (
              <Button
                onClick={() => navigate('/pricing')}
                className="bg-[#00FF00] text-black hover:bg-[#00DD00] font-semibold"
                data-testid="certificate-upgrade-btn"
              >
                <Crown className="w-4 h-4 mr-2" />
                Upgrade to Pro
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white" data-testid="certificate-page">
      <Navbar />

      {/* Controls (hidden on print) */}
      <div className="print:hidden max-w-5xl mx-auto px-4 pt-24 pb-4 flex flex-wrap items-center justify-between gap-3">
        <Button
          variant="outline"
          onClick={() => navigate(`/room/${roomId}`)}
          className="border-gray-700 text-gray-200 hover:bg-gray-800"
          data-testid="certificate-back-btn"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to room
        </Button>

        <div
          className="flex flex-wrap items-center gap-2"
          data-testid="certificate-share-row"
        >
          <span className="text-xs text-gray-500 uppercase tracking-wide mr-1">
            Share
          </span>
          <Button
            onClick={openLinkedIn}
            className="bg-[#0A66C2] text-white hover:bg-[#0855A3] font-semibold"
            data-testid="certificate-share-linkedin"
          >
            <Linkedin className="w-4 h-4 mr-2" />
            LinkedIn
          </Button>
          <Button
            onClick={openTwitter}
            className="bg-black text-white hover:bg-gray-900 font-semibold border border-gray-700"
            data-testid="certificate-share-twitter"
          >
            <Twitter className="w-4 h-4 mr-2" />
            Post on X
          </Button>
          <Button
            onClick={copyShareLink}
            variant="outline"
            className="border-gray-700 text-gray-200 hover:bg-gray-800"
            data-testid="certificate-share-copy"
          >
            {linkCopied ? (
              <>
                <CheckIcon className="w-4 h-4 mr-2" />
                Copied
              </>
            ) : (
              <>
                <Link2 className="w-4 h-4 mr-2" />
                Copy link
              </>
            )}
          </Button>
          <Button
            onClick={handlePrint}
            className="bg-[#00FF00] text-black hover:bg-[#00DD00] font-semibold"
            data-testid="certificate-download-btn"
          >
            <Download className="w-4 h-4 mr-2" />
            Save as PDF
          </Button>
        </div>
      </div>

      {referralCode && (
        <div className="print:hidden max-w-5xl mx-auto px-4 pb-4">
          <p
            className="text-xs text-gray-500"
            data-testid="certificate-share-hint"
          >
            Your referral code <span className="text-[#00FF00] font-mono">{referralCode}</span>{' '}
            is baked into every share — friends who sign up through your link get{' '}
            <span className="text-[#00FF00]">+14 days of Pro</span>, and so do you.
          </p>
        </div>
      )}

      {/* Certificate */}
      <div className="max-w-5xl mx-auto px-4 pb-20 print:p-0 print:max-w-none">
        <div
          className="relative overflow-hidden border border-[#00FF00]/30 rounded-2xl print:rounded-none print:border-0"
          style={{
            background:
              'linear-gradient(145deg, #0F1410 0%, #0A0A0A 50%, #0F1410 100%)',
          }}
          data-testid="certificate-canvas"
        >
          {/* Decorative corners */}
          <div className="absolute top-0 left-0 w-40 h-40 border-l-2 border-t-2 border-[#00FF00]/40 rounded-tl-2xl pointer-events-none" />
          <div className="absolute top-0 right-0 w-40 h-40 border-r-2 border-t-2 border-[#00BFFF]/40 rounded-tr-2xl pointer-events-none" />
          <div className="absolute bottom-0 left-0 w-40 h-40 border-l-2 border-b-2 border-[#00BFFF]/40 rounded-bl-2xl pointer-events-none" />
          <div className="absolute bottom-0 right-0 w-40 h-40 border-r-2 border-b-2 border-[#00FF00]/40 rounded-br-2xl pointer-events-none" />

          {/* Faint radial glow */}
          <div
            className="absolute inset-0 opacity-20 pointer-events-none"
            style={{
              background:
                'radial-gradient(circle at 50% 30%, rgba(0,255,0,0.2) 0%, transparent 55%)',
            }}
          />

          <div className="relative z-10 px-10 sm:px-16 py-16 sm:py-20 text-center">
            <div className="inline-flex items-center gap-2 text-xs tracking-[0.3em] text-[#00FF00] uppercase mb-3">
              <ShieldCheck className="w-4 h-4" />
              Certificate of Completion
            </div>
            <h1
              className="text-5xl sm:text-6xl font-bold tracking-tight mb-2 font-serif"
              style={{ fontFamily: 'Georgia, Cambria, Times New Roman, serif' }}
              data-testid="certificate-title"
            >
              CyberInsti
            </h1>
            <p className="text-gray-400 text-sm mb-10">
              hereby certifies that
            </p>

            <p
              className="text-4xl sm:text-5xl font-bold text-white mb-3"
              data-testid="certificate-username"
            >
              {cert.username}
            </p>
            <p className="text-gray-400 text-sm mb-8">has successfully completed</p>

            <p
              className="text-2xl sm:text-3xl text-[#00FF00] font-semibold mb-10"
              data-testid="certificate-room-title"
            >
              {cert.room.title}
            </p>

            <div className="grid grid-cols-3 gap-6 max-w-2xl mx-auto mb-12 text-xs uppercase tracking-wide">
              <div className="border-t border-gray-700 pt-3">
                <p className="text-gray-500">Category</p>
                <p className="text-white font-semibold text-sm mt-1">
                  {cert.room.category || '—'}
                </p>
              </div>
              <div className="border-t border-gray-700 pt-3">
                <p className="text-gray-500">Difficulty</p>
                <p className="text-white font-semibold text-sm mt-1">
                  {cert.room.difficulty || '—'}
                </p>
              </div>
              <div className="border-t border-gray-700 pt-3">
                <p className="text-gray-500">Points earned</p>
                <p className="text-[#00FF00] font-semibold text-sm mt-1">
                  {cert.room.points}
                </p>
              </div>
            </div>

            <div className="flex justify-between items-end max-w-2xl mx-auto text-xs text-gray-400">
              <div className="text-left">
                <p className="uppercase tracking-wide text-gray-500">Issued on</p>
                <p
                  className="text-white font-semibold mt-1"
                  data-testid="certificate-date"
                >
                  {formatDate(cert.completed_at)}
                </p>
              </div>
              <div className="text-center">
                <div
                  className="font-serif italic text-2xl text-[#00FF00]"
                  style={{
                    fontFamily:
                      'Zapfino, Snell Roundhand, Apple Chancery, cursive',
                  }}
                >
                  CyberInsti
                </div>
                <p className="uppercase tracking-wide text-gray-500 mt-1">
                  {cert.issued_by}
                </p>
              </div>
              <div className="text-right">
                <p className="uppercase tracking-wide text-gray-500">
                  Certificate ID
                </p>
                <p
                  className="text-white font-mono text-[11px] mt-1"
                  data-testid="certificate-id"
                >
                  {cert.certificate_id}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @media print {
          body { background: white; }
        }
      `}</style>
    </div>
  );
};

export default Certificate;
